import React from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { useHistory } from 'react-router';
import { ProductType } from '@tink/core/src/types';
import { RefreshableItem } from '../../../models/refreshableItem';
import { AppState } from '../../../stores/redux-store';
import { ERR_USER_CANCELLED } from '../../../utilities/error';
import { ProviderEntity } from '../../../models/provider';
import { applicationParameters } from '../../../utilities/application-parameters';
import { setApplicationError as setApplicationErrorConnect } from '../../../stores/app-state-store';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { PageView, UserInteractionEvent } from '../../../models/analytics';

import { HandoffCredentialsAction } from '../../../models/handoffSession';
import {
  InteractionElement,
  parentMessenger,
} from '../../../core/Analytics/ParentApplicationEventMessenger';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import { useShouldShowUserHintId } from './hooks/useShouldShowUserHintId';
import { SubmitAuthCredentials } from './SubmitAuthCredentials';
import { SubmitCredentialsPayload } from './hooks/useSubmitCredentials/useSubmitCredentials';

type SubmitCredentialsControllerProps = PropsFromRedux & SubmitCredentialsControllerOwnProps;

export type SubmitCredentialsControllerOwnProps = {
  provider: ProviderEntity;
  refreshableItems: RefreshableItem[];
  onSubmitCredentials: (payload: SubmitCredentialsPayload) => void;
} & BackButton;

const SubmitCredentialsControllerPresenter: React.FC<SubmitCredentialsControllerProps> = ({
  provider,
  onBack,
  userHintId,
  refreshableItems,
  setApplicationError,
  onSubmitCredentials,
}) => {
  const history = useHistory();
  const { tinklinkAnalytics } = useAnalytics();
  const { shouldShowUserHintId } = useShouldShowUserHintId();

  const sendEvent = (
    userInteractionEvent: UserInteractionEvent,
    interactionElement: InteractionElement
  ) => {
    tinklinkAnalytics.sendInteractionEvent({
      action: `${PageView.SUBMIT_CREDENTIALS_SCREEN}/${userInteractionEvent}`,
      view: PageView.SUBMIT_CREDENTIALS_SCREEN,
    });

    parentMessenger.fireInteractionEvent({
      pageView: PageView.SUBMIT_CREDENTIALS_SCREEN,
      element: interactionElement,
    });
  };

  const onHeaderButtonClick = () => {
    if (
      applicationParameters.get('providerName') &&
      applicationParameters.get('productType') !== ProductType.PAY // we need to do it if we want to have one step back in payments
    ) {
      sendEvent(UserInteractionEvent.CLOSE_BUTTON, InteractionElement.CLOSE_BUTTON);
      setApplicationError(ERR_USER_CANCELLED);
    } else {
      history.goBack();
    }
  };

  return (
    <SubmitAuthCredentials
      provider={provider}
      userHintId={shouldShowUserHintId ? userHintId : undefined}
      renderProviderFields
      refreshableItems={refreshableItems}
      onBack={onBack || onHeaderButtonClick}
      onSubmitCredentials={onSubmitCredentials}
      handoffCredentialsAction={HandoffCredentialsAction.ADD_CREDENTIALS}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    userHintId: state.user.idHint,
    credentials: state.credentials.data,
  };
};

const connector = connect(mapStateToProps, {
  setApplicationError: setApplicationErrorConnect,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SubmitCredentialsController = connector(SubmitCredentialsControllerPresenter);
